import { useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Microphone, Stop, ArrowClockwise, UploadSimple } from "@phosphor-icons/react";

export default function VoiceRecorder({ onExtracted }) {
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const [recording, setRecording] = useState(false);
  const [blob, setBlob] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef(null);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const b = new Blob(chunksRef.current, { type: "audio/webm" });
        setBlob(b);
        stream.getTracks().forEach((t) => t.stop());
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
    } catch (e) {
      toast.error("Microphone access denied. Use Upload instead.");
    }
  };

  const stop = () => {
    if (mediaRecorderRef.current?.state === "recording") mediaRecorderRef.current.stop();
    setRecording(false);
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const reset = () => { setBlob(null); setTranscript(""); setElapsed(0); };

  const process = async () => {
    if (!blob) return;
    setProcessing(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, "narration.webm");
      const res = await api.post("/assets/parse-voice", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setTranscript(res.data.transcript || "");
      onExtracted?.(res.data.asset || {});
    } catch (e) {
      toast.error("Processing failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setProcessing(false);
    }
  };

  const uploadFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBlob(f);
  };

  const fmtTime = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  return (
    <div data-testid="voice-recorder" className="space-y-6">
      <div>
        <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-2">
          Narrative Ingest · Voice
        </div>
        <h2 className="font-serif-display text-3xl text-[#0A192F]">Walk the room. Describe the object.</h2>
        <p className="text-sm text-[#5C5F58] mt-3 max-w-2xl">
          Speak naturally: &quot;A vintage Rolex Submariner from 1987, serial 8234567, stored in the master bedroom safe, mint condition, purchased from Christie&apos;s for eight thousand dollars.&quot; Whisper will transcribe; GPT will parse.
        </p>
      </div>

      <div className="bg-[#F9F8F6] border border-[#E0DDD6] p-8">
        <div className="flex flex-col items-center gap-6">
          <div className={`w-24 h-24 rounded-full border-2 flex items-center justify-center ${recording ? "border-[#6B1718] bg-[#6B1718]/5 animate-pulse" : "border-[#1A3622] bg-white"}`}>
            <Microphone size={36} weight={recording ? "fill" : "regular"} color={recording ? "#6B1718" : "#1A3622"} />
          </div>
          <div className="font-mono-ledger text-xl tabular-nums text-[#0A192F]">{fmtTime(elapsed)}</div>

          <div className="flex gap-2">
            {!recording && !blob && (
              <button onClick={start} data-testid="record-start" className="bg-[#1A3622] text-[#F9F8F6] px-5 py-2.5 text-sm border border-[#0F2818] hover:bg-[#0F2818]">
                Start Recording
              </button>
            )}
            {recording && (
              <button onClick={stop} data-testid="record-stop" className="bg-[#6B1718] text-white px-5 py-2.5 text-sm border border-[#6B1718] inline-flex items-center gap-2">
                <Stop size={16} weight="fill" /> Stop
              </button>
            )}
            {blob && !recording && (
              <>
                <button onClick={process} disabled={processing} data-testid="process-audio"
                  className="bg-[#1A3622] text-[#F9F8F6] px-5 py-2.5 text-sm border border-[#0F2818] hover:bg-[#0F2818] disabled:opacity-60">
                  {processing ? "Transcribing & parsing…" : "Process Recording"}
                </button>
                <button onClick={reset} className="px-5 py-2.5 text-sm border border-[#E0DDD6] bg-white inline-flex items-center gap-2" data-testid="reset-recording">
                  <ArrowClockwise size={16} /> Reset
                </button>
              </>
            )}
          </div>

          <div className="flex items-center gap-3 text-xs text-[#5C5F58]">
            <span className="h-px w-8 bg-[#C9C4B8]" />
            <span>or</span>
            <span className="h-px w-8 bg-[#C9C4B8]" />
          </div>

          <label className="inline-flex items-center gap-2 px-4 py-2 text-xs border border-[#E0DDD6] bg-white cursor-pointer hover:border-[#1A3622]">
            <UploadSimple size={14} /> Upload an audio file (mp3/m4a/wav/webm)
            <input type="file" accept="audio/*" onChange={uploadFile} className="hidden" data-testid="upload-audio" />
          </label>
        </div>
      </div>

      {transcript && (
        <div className="bg-white border border-[#E0DDD6] p-5" data-testid="transcript-display">
          <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-2">
            Transcript
          </div>
          <p className="text-sm text-[#0A192F] leading-relaxed whitespace-pre-wrap">{transcript}</p>
          <p className="text-xs text-[#5C5F58] mt-3 italic">Draft sent to form. Review and save.</p>
        </div>
      )}
    </div>
  );
}
